import {
  ImageGenerateJobInputSchema,
  type ImageGenerateJobOutput,
} from '@/entities/jobs/imageGenerate';
import { PromptFillJobOutputSchema } from '@/entities/jobs/promptFill';
import { runImage } from '@/shared/api/image';
import { newId } from '@/shared/lib/id';
import { predictionActualCost } from '@/shared/pricing';
import type { Runner, RunnerResult } from './types';
import { uploadProductImages } from './uploadProductImages';

export const imageGenerateRunner: Runner = async (
  job,
  ctx,
): Promise<RunnerResult<ImageGenerateJobOutput>> => {
  const input = ImageGenerateJobInputSchema.parse(job.input);
  const promptFill = await ctx.getJobOutput(input.promptFillJobId, PromptFillJobOutputSchema);
  const rp = promptFill.resolved;

  // Upload any not-yet-uploaded product images for this brand (once per blob).
  const productUrls = rp.needsProductImages
    ? await uploadProductImages(ctx, input.brandId, input.productImageIds)
    : [];

  const result = await runImage(ctx.client, {
    modelId: input.imageModelId,
    prompt: rp.prompt,
    aspectRatio: rp.aspectRatio,
    longestSidePx: input.imageResolutionPx,
    numImages: rp.numImages,
    ...(input.seed !== undefined && { seed: input.seed }),
    ...(rp.needsProductImages && { imageUrls: productUrls }),
    signal: ctx.signal,
  });

  const predId = newId('pred');
  const now = Date.now();
  await ctx.db.predictions.add({
    id: predId,
    replicateId: result.replicateId,
    modelSlug: input.imageModelId,
    kind: 'image',
    status: 'succeeded',
    output: result.imageUrls,
    ...(result.predictTimeSec !== undefined && { predictTimeSec: result.predictTimeSec }),
    startedAt: now,
    completedAt: Date.now(),
  });

  const generatedImageIds: string[] = [];
  for (const url of result.imageUrls) {
    const resp = await fetch(url, { signal: ctx.signal });
    if (!resp.ok) {
      throw new Error(`Failed to download generated image (${resp.status} ${resp.statusText}).`);
    }
    const blob = await resp.blob();
    const id = newId('gen');
    await ctx.db.generatedImages.add({
      id,
      jobId: job.id,
      predictionId: predId,
      templateId: rp.templateId,
      templateName: rp.templateName,
      blob,
      contentType: blob.type || 'image/jpeg',
      createdAt: Date.now(),
    });
    generatedImageIds.push(id);
  }

  const actualCostUsd = predictionActualCost({
    modelSlug: input.imageModelId,
    kind: 'image',
    ...(result.predictTimeSec !== undefined && { predictTimeSec: result.predictTimeSec }),
  });

  return {
    output: { generatedImageIds, predictionId: predId },
    replicateId: result.replicateId,
    usage: {
      ...(result.predictTimeSec !== undefined && { predictTimeSec: result.predictTimeSec }),
    },
    actualCostUsd,
  };
};
